/*
 * Copyright (C) 2018 - 2023 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { useContext, useEffect, useState } from 'react';
import singleDeviceStyles from '../../styles.module.css';
import { DeviceDetailsContext } from '../../index';
import {
  Button,
  Row,
  Col,
  Card,
  Avatar,
  notification,
  Popconfirm,
  Tooltip,
  Collapse,
  Spin,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import styles from './styles.module.css';
import axios from 'axios';
import config from '../../../../../../../public/conf/config.json';
import { handleApiError } from '../../../../../../services/utils/errorHandler';
import Filter from '../../../../components/Filter';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';

const colors = [
  '#f0932b',
  '#badc58',
  '#6ab04c',
  '#22a6b3',
  '#0abde3',
  '#e84393',
  '#9b59b6',
  '#eb4d4b',
  '#3498db',
  '#f9ca24',
];

const { Panel } = Collapse;
const Apps = () => {
  const deviceDetails = useContext(DeviceDetailsContext);
  const type = deviceDetails.deviceType;
  const ownership = deviceDetails.device.enrolmentInfo.ownership;
  // TODO: Move the agent version hard corded to the config json
  const agentVersion = deviceDetails.device.deviceInfo.deviceDetailsMap.AGENT_VERSION?.replace(
    /\./g,
    '',
  );
  const [applications, setApplications] = useState(
    deviceDetails.device.applications,
  );
  const { t } = useTranslation();
  let searchApps = [];
  let [androidApplications, setAndroidApplications] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [access, setAccess] = useState(true);
  const limit = 10;

  useEffect(() => {
    // Update the current route's key
    deviceDetails.setCurrentRoute('apps');
    if (type === 'android') {
      fetchDeviceApplications(0, limit, res => {
        setAndroidApplications(res);
        setApplications(res);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  let isRemoved = false;
  if (deviceDetails.device.enrolmentInfo.status === 'REMOVED') {
    isRemoved = true;
  }
  const onSearch = (params, values) => {
    if (Object.keys(values).length === 0) {
      if (type === 'android') {
        setApplications(androidApplications);
      } else {
        setApplications(deviceDetails.device.applications);
      }
      return;
    }
    let applicationList;
    const search = values.applicationName.toLowerCase();
    const searchRegex = new RegExp(`\\b${search}[a-zA-Z]*\\b`, 'i');
    if (type === 'android') {
      applicationList = androidApplications;
    } else {
      applicationList = deviceDetails.devcice.application;
    }
    for (let i = 0; i < applicationList.length; i++) {
      let app = applicationList[i];
      let appName = app.name.toLowerCase().replace(/%20/g, ' ');
      if (searchRegex.test(appName)) {
        searchApps.push(app);
      }
    }
    setApplications(searchApps);
  };

  let fields = [
    {
      name: 'applicationName',
      type: 'input',
      placeholder: t('label_appName'),
      label: t('label_appName'),
    },
  ];

  const handleOk = application => {
    onConfirmUninstall(application);
  };

  const onConfirmUninstall = application => {
    const packageName = application.applicationIdentifier;
    const deviceIdentifier = deviceDetails.deviceIdentifier;
    const headers = {
      'Content-Type': 'application/json',
    };
    /* send request to the invoker*/
    axios
      .post(
        window.location.origin +
          config.serverConfig.invoker.uri +
          config.serverConfig.invoker.deviceMgt +
          `/devices/${type}/${deviceIdentifier}/uninstallation?packageName=` +
          packageName,
        {},
        {
          headers: headers,
        },
      )
      .then(res => {
        if (res.status === 200 || res.status === 201) {
          notification.success({
            message: t('api_successMsg'),
            duration: 4,
            description: t('api_uninstallTriggeredMsg'),
          });
        }
        if (res.status === 400) {
          notification.error({
            message: t('api_errorMsg'),
            duration: 4,
            description: t('api_installAppError'),
          });
        }
      })
      .catch(error => {
        handleApiError(error, t('api_uninstallAppError'), t);
      });
  };

  const sendAppListCommand = () => {
    const headers = {
      'Content-Type': 'application/json',
    };
    // Add an app list command to update the app list with the change
    const dataToSend = [deviceDetails.deviceIdentifier];
    axios
      .post(
        window.location.origin +
          config.serverConfig.invoker.uri +
          '/device-mgt/' +
          type +
          '/v1.0/admin/devices/applications?type=device',
        dataToSend,
        {
          headers: headers,
        },
      )
      .then(res => {
        if (res.status === 201) {
          notification.success({
            message: t('api_successMsg'),
            duration: 4,
            description: t('api_AppListTriggeredMsg'),
          });
        }
        if (res.status === 400) {
          notification.error({
            message: t('api_errorMsg'),
            duration: 4,
            description: t('api_appListDataError'),
          });
        }
      })
      .catch(error => {
        handleApiError(error, t('api_appListDataError'), t);
      });
  };

  const handleSuspendApp = (application, isActive) => {
    const packageName = application.applicationIdentifier;
    const deviceIdentifier = deviceDetails.deviceIdentifier;
    const headers = {
      'Content-Type': 'application/json',
    };
    const payload = {
      disabledPackages: {
        disabledPackages: [packageName],
        ids: [deviceIdentifier],
      },
      enabledPackages: {
        enabledPackages: [packageName],
        ids: [deviceIdentifier],
      },
    };
    const data = isActive ? payload.disabledPackages : payload.enabledPackages;

    // /* send suspend package operation request*/
    axios
      .post(
        window.location.origin +
          config.serverConfig.invoker.uri +
          '/device-mgt/' +
          type +
          '/v1.0/admin/devices/suspend-package?type=device',
        data,
        {
          headers: headers,
        },
      )
      .then(res => {
        if (res.status === 201) {
          notification.success({
            message: t('api_successMsg'),
            duration: 4,
            description: t('api_suspendPackageTriggeredMsg'),
          });
          setTimeout(() => {
            sendAppListCommand();
          }, 500);

          application.isActive = !isActive;
          deviceDetails.device.applications = application;
          setApplications(applications);
        }
        if (res.status === 400) {
          notification.error({
            message: t('api_errorMsg'),
            duration: 4,
            description: t('api_suspendPackageDataError'),
          });
        }
      })
      .catch(error => {
        handleApiError(error, t('api_suspendPackageDataError'), t);
      });
  };

  const handleClear = application => {
    const packageName = application.applicationIdentifier;
    const deviceIdentifier = deviceDetails.deviceIdentifier;
    const headers = {
      'Content-Type': 'application/json',
    };
    /* send request to the invoker*/
    axios
      .post(
        window.location.origin +
          config.serverConfig.invoker.uri +
          '/device-mgt/' +
          type +
          '/v1.0/admin/devices/clear-application-data?',
        {
          deviceID: deviceIdentifier,
          deviceType: type,
          operation: {
            appIdentifier: packageName,
          },
        },
        {
          headers: headers,
        },
      )
      .then(res => {
        if (res.status === 200) {
          notification.success({
            message: t('api_successMsg'),
            duration: 4,
            description: t('api_clearAppTriggeredMsg'),
          });
        }
        if (res.status === 400) {
          notification.error({
            message: t('api_errorMsg'),
            duration: 4,
            description: t('api_clearAppDataError'),
          });
        }
      })
      .catch(error => {
        handleApiError(error, t('api_clearAppDataError'), t);
      });
  };

  const fetchDeviceApplications = (offset, limit, callbackFunction) => {
    setLoading(true);
    let apiURL =
      window.location.origin +
      config.serverConfig.invoker.uri +
      '/device-mgt/' +
      deviceDetails.deviceType +
      `/v1.0/admin/devices/${deviceDetails.deviceIdentifier}/device-applications?offset=${offset}&limit=${limit}`;
    axios
      .get(apiURL)
      .then(result => {
        let apps = [];
        if (result.data.data.length !== 0) {
          apps = result.data.data;
        }
        callbackFunction(apps);
      })
      .catch(error => {
        handleApiError(error, t('api_retrieveDeviceApplicationsError'), t);
        setLoading(false);
        setHasMore(false);
        setAccess(false);
      });
  };

  const handleInfiniteOnLoad = count => {
    if (type === 'android' && access) {
      const offset = count * limit;
      setLoading(true);
      fetchDeviceApplications(offset, limit, res => {
        if (res.length > 0) {
          androidApplications = androidApplications.concat(res);
          setAndroidApplications(androidApplications);
          setApplications(androidApplications);
          setLoading(false);
        } else {
          setHasMore(false);
          setLoading(false);
        }
      });
    } else {
      setHasMore(false);
      setLoading(false);
    }
  };

  return (
    <InfiniteScroll
      key={deviceDetails.deviceIdentifier}
      initialLoad={false}
      pageStart={0}
      loadMore={handleInfiniteOnLoad}
      hasMore={!loading && hasMore}
      useWindow={true}
      loader={loading}
    >
      <div className={singleDeviceStyles.container}>
        <div>
          <div className={styles.table}>
            <Collapse
              bordered={false}
              expandIcon={() => <SearchOutlined />}
              className={styles.searchCollapse}
            >
              <Panel
                header={t('form_searchApps')}
                key="1"
                style={{ borderColor: '#fff' }}
              >
                <Filter
                  filters={fields}
                  callback={(params, values) => onSearch({}, values)}
                />
              </Panel>
            </Collapse>
          </div>
        </div>
        <Spin spinning={loading} tip="Loading...">
          <Row>
            {!isRemoved ? (
              <Col md={8}>
                <a href="/store" target="_blank">
                  <Card className={styles.installCard} hoverable={true}>
                    <Card.Meta
                      avatar={
                        <Avatar
                          shape="square"
                          size="large"
                          className={styles.avatar}
                          icon={<PlusOutlined />}
                        />
                      }
                      title={t('label_installApps')}
                      description={t('installApp_descriptionTxt')}
                    />
                  </Card>
                </a>
              </Col>
            ) : null}
            {type === 'android' &&
              applications != null &&
              applications.length > 0 && (
                <>
                  {applications.map(application => {
                    // Generating a color based on the application name
                    const color = colors[application.name.length % 10];
                    // Use the first character of the app name as the avatar's letter
                    const avatarLetter = application.name
                      .charAt(0)
                      .toUpperCase();
                    return (
                      <Col md={8} key={application.id}>
                        <Card className={styles.appCard}>
                          <Card.Meta
                            avatar={
                              application.imageUrl ? (
                                <span className={styles.imageContainer}>
                                  <img
                                    src={application.imageUrl}
                                    className={styles.appIcon}
                                  />
                                </span>
                              ) : (
                                <Avatar
                                  style={{
                                    backgroundColor: color,
                                  }}
                                  shape="square"
                                  size="large"
                                  className={styles.avatar}
                                >
                                  {avatarLetter}
                                </Avatar>
                              )
                            }
                            // Decode URL encoded application name
                            title={
                              <Tooltip
                                placement="topLeft"
                                title={decodeURI(application.name)}
                                autoAdjustOverflow={true}
                              >
                                <div className={styles.description}>
                                  {decodeURI(application.name)}
                                </div>
                              </Tooltip>
                            }
                            description={
                              <Tooltip
                                placement="rightBottom"
                                title={application.applicationIdentifier}
                                autoAdjustOverflow={true}
                              >
                                <div className={styles.description}>
                                  {application.applicationIdentifier}
                                </div>
                              </Tooltip>
                            }
                          />
                          <Card.Meta
                            style={{
                              marginLeft: 57,
                              paddingTop: 5,
                            }}
                            // Showing the application version in the Card model
                            description={
                              <Tooltip
                                placement="rightBottom"
                                title={application.version}
                                autoAdjustOverflow={true}
                              >
                                <div className={styles.description}>
                                  {t('label_version')} : {application.version}
                                </div>
                              </Tooltip>
                            }
                          />
                          {application.applicationIdentifier !==
                            'io.entgra.iot.agent' && (
                            <div>
                              <Tooltip
                                placement="bottom"
                                title={t('label_uninstallApp')}
                                autoAdjustOverflow={true}
                              >
                                <Popconfirm
                                  placement="topLeft"
                                  title={
                                    t('uninstallApp_confirmTxt') +
                                    decodeURI(application.name) +
                                    ' ? '
                                  }
                                  onConfirm={() => {
                                    handleOk(application);
                                  }}
                                  okText={t('label_ok')}
                                  cancelText={t('label_cancel')}
                                  disabled={isRemoved}
                                >
                                  <Button
                                    className={styles.subButtons}
                                    type="default"
                                    size="small"
                                    disabled={isRemoved}
                                  >
                                    {t('label_uninstall')}
                                  </Button>
                                </Popconfirm>
                              </Tooltip>
                              {ownership !== 'BYOD' && (
                                <Tooltip
                                  placement="bottom"
                                  title={t('label_clearAppData')}
                                  autoAdjustOverflow={true}
                                >
                                  <Popconfirm
                                    placement="topLeft"
                                    title={t('clearAppData_confirmTxt')}
                                    onConfirm={() => {
                                      handleClear(application);
                                    }}
                                    okText={t('label_ok')}
                                    cancelText={t('label_cancel')}
                                  >
                                    <Button
                                      className={styles.subButtons}
                                      type="default"
                                      size="small"
                                    >
                                      {t('label_clear')}
                                    </Button>
                                  </Popconfirm>
                                </Tooltip>
                              )}
                              {ownership !== 'BYOD' && agentVersion > 4050002 && (
                                <Tooltip
                                  placement="bottom"
                                  title={
                                    application.isActive
                                      ? t('label_disable_package_tag')
                                      : t('label_enable_package_tag')
                                  }
                                  autoAdjustOverflow={true}
                                >
                                  <Popconfirm
                                    placement="topLeft"
                                    title={t('clearSuspend_confirmTxt')}
                                    onConfirm={() => {
                                      handleSuspendApp(
                                        application,
                                        application.isActive,
                                      );
                                    }}
                                    okText={t('label_ok')}
                                    cancelText={t('label_cancel')}
                                  >
                                    <Button
                                      className={styles.subButtons}
                                      type="default"
                                      size="small"
                                    >
                                      {application.isActive
                                        ? t('label_disable')
                                        : t('label_enable')}
                                    </Button>
                                  </Popconfirm>
                                </Tooltip>
                              )}
                            </div>
                          )}
                        </Card>
                      </Col>
                    );
                  })}
                </>
              )}
            {type !== 'android' && (
              <>
                {applications.map(application => {
                  // Generating a color based on the application name
                  const color = colors[application.name.length % 10];
                  // Use the first character of the app name as the avatar's letter
                  const avatarLetter = application.name.charAt(0).toUpperCase();
                  return (
                    <Col md={8} key={application.id}>
                      <Card className={styles.appCard}>
                        <Card.Meta
                          avatar={
                            application.imageUrl ? (
                              <span className={styles.imageContainer}>
                                <img
                                  src={application.imageUrl}
                                  className={styles.appIcon}
                                />
                              </span>
                            ) : (
                              <Avatar
                                style={{
                                  backgroundColor: color,
                                }}
                                shape="square"
                                size="large"
                                className={styles.avatar}
                              >
                                {avatarLetter}
                              </Avatar>
                            )
                          }
                          // Decode URL encoded application name
                          title={
                            <Tooltip
                              placement="topLeft"
                              title={decodeURI(application.name)}
                              autoAdjustOverflow={true}
                            >
                              <div className={styles.description}>
                                {decodeURI(application.name)}
                              </div>
                            </Tooltip>
                          }
                          description={
                            <Tooltip
                              placement="rightBottom"
                              title={application.applicationIdentifier}
                              autoAdjustOverflow={true}
                            >
                              <div className={styles.description}>
                                {application.applicationIdentifier}
                              </div>
                            </Tooltip>
                          }
                        />
                        <Card.Meta
                          style={{
                            marginLeft: 57,
                            paddingTop: 5,
                          }}
                          // Showing the application version in the Card model
                          description={
                            <Tooltip
                              placement="rightBottom"
                              title={application.version}
                              autoAdjustOverflow={true}
                            >
                              <div className={styles.description}>
                                {t('label_version')} : {application.version}
                              </div>
                            </Tooltip>
                          }
                        />
                      </Card>
                    </Col>
                  );
                })}
              </>
            )}
          </Row>
        </Spin>
      </div>
    </InfiniteScroll>
  );
};
export default Apps;
